import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getAllPartners } from "../api/trips";
import { sendPartnerRequest, getReceivedRequests, getSentRequests, acceptPartnerRequest, rejectPartnerRequest } from "../api/users";
import { sendRequestToPartner, getReceivedQueries, getMyAgencyRequests, acceptQuery, rejectQuery } from "../api/agencies";
import { isAuthenticated, getStoredUser } from "../api/auth";
import { createRating } from "../api/ratings";
import { triggerNotificationRefresh } from "../utils/notificationRefresh";
import CustomAlert from "../components/CustomAlert";
import RatingModal from "../components/RatingModal";
import RatingsViewModal from "../components/RatingsViewModal";
import { BACKEND_BASE } from "../config";
import "./FindPartner.css";

export default function FindPartner() {
  const [partners, setPartners] = useState([]);
  const [filteredPartners, setFilteredPartners] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuth, setIsAuth] = useState(false);
  const [userRole, setUserRole] = useState(""); // partner or agency
  const [showFilters, setShowFilters] = useState(false);
  
  // New filter states
  const [filters, setFilters] = useState({
    destination: "all",
    minBudget: "",
    maxBudget: "",
    startDate: "",
    endDate: "",
    location: "all",
    minAge: "",
    maxAge: "",
    gender: "all",
    connectionStatus: "all", // all, connected, pending, none
    tripStatus: "all" // all, planning, active, completed
  });
  
  // Request management
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [requestMessage, setRequestMessage] = useState("");
  const [sendingRequest, setSendingRequest] = useState(false);
  
  // Contact details modal for connected users
  const [showContactModal, setShowContactModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  
  // Rating modal
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [ratingTarget, setRatingTarget] = useState(null); // {userId, userName, userRole}
  
  // Ratings view modal
  const [showRatingsViewModal, setShowRatingsViewModal] = useState(false);
  const [ratingsViewTarget, setRatingsViewTarget] = useState(null); // {userId, userName}
  
  // Requests tabs
  const [activeTab, setActiveTab] = useState("discover"); // discover, sent, received
  const [sentRequests, setSentRequests] = useState([]);
  const [receivedRequests, setReceivedRequests] = useState([]);
  
  // Custom alert state
  const [alertConfig, setAlertConfig] = useState({
    show: false,
    title: '',
    message: '',
    type: 'info'
  });

  const showAlert = (title, message, type = 'info') => {
    setAlertConfig({
      show: true,
      title,
      message,
      type
    });
  };

  const closeAlert = () => {
    setAlertConfig({
      ...alertConfig,
      show: false
    });
  };

  useEffect(() => {
    // Check authentication first
    const authenticated = isAuthenticated();
    setIsAuth(authenticated);
    
    if (authenticated) {
      const currentUser = getStoredUser();
      setUserRole(currentUser?.role || "partner");
      loadPartners();
      loadRequests();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    filterPartners();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partners, searchTerm, filters]);

  async function loadPartners() {
    try {
      setLoading(true);
      const data = await getAllPartners();
      
      // Partners are already grouped with their trips, no need to flatten
      setPartners(data);
      setFilteredPartners(data);
      setError(null);
    } catch (err) {
      console.error("Error loading partners:", err);
      setError("Failed to load partners. Please try again.");
    } finally {
      setLoading(false);
    }
  }
  
  async function loadRequests() {
    try {
      const currentUser = getStoredUser();
      const userRole = currentUser?.role || 'partner';
      
      if (userRole === 'agency') {
        // For agencies: load sent requests and received queries
        const [sent, received] = await Promise.all([
          getMyAgencyRequests(), // GET /api/agencies/my-requests
          getReceivedQueries()    // GET /api/agencies/queries/received
        ]);
        setSentRequests(sent || []);
        setReceivedRequests(received || []);
      } else {
        // For partners: load sent and received partner requests
        const [sent, received] = await Promise.all([
          getSentRequests(),
          getReceivedRequests()
        ]);
        setSentRequests(sent || []);
        setReceivedRequests(received || []);
      }
    } catch (err) {
      console.error("Error loading requests:", err);
    }
  }

  function filterPartners() {
    let filtered = partners;

    // Text search filter
    if (searchTerm) {
      filtered = filtered.filter(partner => {
        const name = partner.user?.name || "";
        const bio = partner.user?.bio || "";
        const hasMatchingTrip = partner.trips?.some(trip => 
          trip.destination?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          trip.interests?.toLowerCase().includes(searchTerm.toLowerCase())
        );
        return name.toLowerCase().includes(searchTerm.toLowerCase()) ||
               bio.toLowerCase().includes(searchTerm.toLowerCase()) ||
               hasMatchingTrip;
      });
    }

    // Destination filter
    if (filters.destination !== "all") {
      filtered = filtered.filter(partner =>
        partner.trips?.some(trip =>
          trip.destination?.toLowerCase().includes(filters.destination.toLowerCase())
        )
      );
    }

    // Budget filter
    if (filters.minBudget || filters.maxBudget) {
      filtered = filtered.filter(partner =>
        partner.trips?.some(trip => {
          const budget = parseFloat(trip.budget) || 0;
          const min = parseFloat(filters.minBudget) || 0;
          const max = parseFloat(filters.maxBudget) || Infinity;
          return budget >= min && budget <= max;
        })
      );
    }

    // Date filter
    if (filters.startDate || filters.endDate) {
      filtered = filtered.filter(partner =>
        partner.trips?.some(trip => {
          const tripDate = new Date(trip.trip_date);
          const start = filters.startDate ? new Date(filters.startDate) : new Date('1900-01-01');
          const end = filters.endDate ? new Date(filters.endDate) : new Date('2100-12-31');
          return tripDate >= start && tripDate <= end;
        })
      );
    }

    // Location filter
    if (filters.location !== "all") {
      filtered = filtered.filter(partner =>
        partner.user?.current_location?.toLowerCase().includes(filters.location.toLowerCase())
      );
    }

    // Age filter
    if (filters.minAge || filters.maxAge) {
      filtered = filtered.filter(partner => {
        const age = partner.user?.age || 0;
        const min = parseInt(filters.minAge) || 0;
        const max = parseInt(filters.maxAge) || 150;
        return age >= min && age <= max;
      });
    }

    // Gender filter
    if (filters.gender !== "all") {
      filtered = filtered.filter(partner =>
        partner.user?.gender?.toLowerCase() === filters.gender.toLowerCase()
      );
    }

    // Connection status filter
    if (filters.connectionStatus !== "all") {
      filtered = filtered.filter(partner => {
        const status = partner.connection_status || "none";
        
        // Map 'connected' filter value to 'accepted' backend value
        if (filters.connectionStatus === "connected") {
          return status === "accepted";
        }
        
        return status === filters.connectionStatus;
      });
    }

    // Trip status filter
    if (filters.tripStatus !== "all") {
      filtered = filtered.filter(partner =>
        partner.trips?.some(trip =>
          trip.status?.toLowerCase() === filters.tripStatus.toLowerCase()
        )
      );
    }

    setFilteredPartners(filtered);
  }

  // Get unique values for filter dropdowns
  const destinations = ["all", ...new Set(
    partners.flatMap(p => p.trips?.map(t => t.destination) || []).filter(Boolean)
  )];

  const locations = ["all", ...new Set(
    partners.map(p => p.user?.current_location).filter(Boolean)
  )];

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const resetFilters = () => {
    setFilters({
      destination: "all",
      minBudget: "",
      maxBudget: "",
      startDate: "",
      endDate: "",
      location: "all",
      minAge: "",
      maxAge: "",
      gender: "all",
      connectionStatus: "all",
      tripStatus: "all"
    });
    setSearchTerm("");
  };

  function openRequestModal(partner) {
    setSelectedPartner(partner);
    
    // Get current user's role
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    const userRole = currentUser?.role || 'partner';
    
    // Generate message based on user role and trips
    let message = `Hi ${partner.user.name}! `;
    
    if (userRole === 'agency') {
      // Agency sending request to partner
      if (partner.trips && partner.trips.length > 0) {
        if (partner.trips.length === 1) {
          message += `I represent a travel agency and noticed you're planning to visit ${partner.trips[0].destination}. We'd love to assist you with travel arrangements and services for your trip!`;
        } else {
          const destinations = partner.trips.map(t => t.destination).join(', ');
          message += `I represent a travel agency and noticed you have trips planned to ${destinations}. We'd love to help you with travel arrangements and make your journeys memorable!`;
        }
      } else {
        message += `I represent a travel agency and would love to connect with you to discuss travel opportunities and services we can offer!`;
      }
    } else {
      // Partner sending request to another partner
      if (partner.trips && partner.trips.length > 0) {
        if (partner.trips.length === 1) {
          message += `I see you're planning to visit ${partner.trips[0].destination}. Would love to travel together!`;
        } else {
          const destinations = partner.trips.map(t => t.destination).join(', ');
          message += `I see you have trips planned to ${destinations}. Would love to connect and explore traveling together!`;
        }
      } else {
        message += `Would love to connect and explore traveling together!`;
      }
    }
    
    setRequestMessage(message);
    setShowRequestModal(true);
  }

  function openContactModal(partner) {
    setSelectedContact(partner);
    setShowContactModal(true);
  }

  async function handleSendRequest() {
    if (!requestMessage.trim()) {
      showAlert('Message Required', 'Please enter a message before sending your request.', 'warning');
      return;
    }

    try {
      setSendingRequest(true);
      
      // Get current user's role
      const currentUser = getStoredUser();
      const userRole = currentUser?.role || 'partner';
      
      // Use different API based on user role
      if (userRole === 'agency') {
        // Agency sending request to partner
        await sendRequestToPartner(selectedPartner.id, requestMessage);
        showAlert('Request Sent! 🎉', 'Your request has been sent to the partner successfully!', 'success');
      } else {
        // Partner sending request to another partner
        await sendPartnerRequest(selectedPartner.id, requestMessage);
        showAlert('Request Sent! 🎉', 'Your connection request has been sent successfully! Wait for their response.', 'success');
      }
      
      setShowRequestModal(false);
      setRequestMessage("");
      // Reload both requests and partners to update status
      await Promise.all([loadRequests(), loadPartners()]);
    } catch (err) {
      console.error("Error sending request:", err);
      const errorMessage = err.response?.data?.detail || "Failed to send request. Please try again.";
      
      // Check if error is about needing to create a trip
      if (errorMessage.includes('trip') || errorMessage.includes('Trip')) {
        showAlert(
          'Create a Trip First! ✈️', 
          errorMessage,
          'warning'
        );
      } else {
        showAlert('Request Failed', errorMessage, 'error');
      }
    } finally {
      setSendingRequest(false);
    }
  }

  async function handleAcceptRequest(requestId) {
    try {
      const currentUser = getStoredUser();
      const role = currentUser?.role || 'partner';
      
      if (role === 'agency') {
        // For agencies: accepting a query from a partner
        await acceptQuery(requestId, "We are happy to work with you! Looking forward to helping you plan your trip.");
        showAlert(
          'Query Accepted! ✓', 
          'You have accepted this inquiry. The partner can now see your response.', 
          'success'
        );
      } else {
        // For partners: accepting a connection request
        await acceptPartnerRequest(requestId);
        showAlert(
          'Request Accepted! ✓', 
          'You can now see their contact information and start planning your trip together!', 
          'success'
        );
      }
      
      // Reload both requests and partners to update status
      await Promise.all([loadRequests(), loadPartners()]);
    } catch (err) {
      console.error("Error accepting request:", err);
      showAlert('Accept Failed', 'Failed to accept request. Please try again.', 'error');
    }
  }

  async function handleRejectRequest(requestId) {
    try {
      const currentUser = getStoredUser();
      const role = currentUser?.role || 'partner';
      
      if (role === 'agency') {
        // For agencies: rejecting a query from a partner
        await rejectQuery(requestId, "Sorry, we cannot accommodate this request at this time.");
        showAlert('Query Rejected', 'You have declined this inquiry.', 'info');
      } else {
        // For partners: rejecting a connection request
        await rejectPartnerRequest(requestId);
        showAlert('Request Rejected', 'The connection request has been declined.', 'info');
      }
      
      // Reload both requests and partners to update status
      await Promise.all([loadRequests(), loadPartners()]);
    } catch (err) {
      console.error("Error rejecting request:", err);
      showAlert('Reject Failed', 'Failed to reject request. Please try again.', 'error');
    }
  }

  async function handleRateUser(userId, userName, userRole) {
    setRatingTarget({ userId, userName, userRole });
    setShowRatingModal(true);
  }

  async function handleSubmitRating(rating, feedbackMessage) {
    try {
      await createRating(ratingTarget.userId, rating, feedbackMessage);
      showAlert(
        'Rating Submitted! ⭐', 
        `Thank you for rating ${ratingTarget.userName}! Your feedback helps the community.`, 
        'success'
      );
      triggerNotificationRefresh();
      // Reload data to reflect the new rating
      loadPartners();
      loadRequests();
    } catch (err) {
      console.error("Error submitting rating:", err);
      const errorMessage = err.response?.data?.detail || "Failed to submit rating. Please try again.";
      showAlert('Rating Failed', errorMessage, 'error');
    }
  }

  function handleViewRatings(userId, userName) {
    setRatingsViewTarget({ userId, userName });
    setShowRatingsViewModal(true);
  }

  return (
    <div className="find-partner-container">
      {/* Header Section */}
      <div className="find-partner-header">
        <div className="header-content">
          <h1 className="page-title">Find Your Travel Buddy</h1>
          <p className="page-subtitle">
            Connect with verified travelers and start planning your next adventure together
          </p>
        </div>
      </div>

      {/* Check if user is authenticated */}
      {!isAuth ? (
        <div className="auth-required-section">
          <div className="container">
            <div className="auth-required-card">
              <div className="auth-icon">🔒</div>
              <h2>Authentication Required</h2>
              <p>Please login or register to discover travel partners and connect with fellow travelers.</p>
              <div className="auth-actions">
                <Link to="/login" className="auth-btn login-btn">
                  <span>Login</span>
                  <span className="btn-icon">→</span>
                </Link>
                <Link to="/register" className="auth-btn register-btn">
                  <span>Register</span>
                  <span className="btn-icon">✓</span>
                </Link>
              </div>
              <div className="auth-benefits">
                <h3>Why Register?</h3>
                <ul>
                  <li>🌍 Connect with verified travel partners</li>
                  <li>🤝 Send and receive connection requests</li>
                  <li>📱 Share contact info with matched travelers</li>
                  <li>✈️ Plan amazing trips together</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Tabs */}
          <div className="tabs-section">
            <div className="container">
              <div className="tabs">
                <button
                  className={`tab ${activeTab === 'discover' ? 'active' : ''}`}
                  onClick={() => setActiveTab('discover')}
                >
                  🔍 Discover Partners
                </button>
                <button
                  className={`tab ${activeTab === 'received' ? 'active' : ''}`}
                  onClick={() => setActiveTab('received')}
                >
                  📨 Received Requests ({receivedRequests.filter(r => r.status === 'pending').length})
                </button>
                <button
                  className={`tab ${activeTab === 'sent' ? 'active' : ''}`}
                  onClick={() => setActiveTab('sent')}
                >
                  📤 Sent Requests ({sentRequests.filter(r => r.status === 'pending').length})
                </button>
              </div>
        </div>
      </div>

      {/* Discover Tab */}
      {activeTab === 'discover' && (
        <>
          {/* Filters Section */}
          <div className="filters-section">
            <div className="container">
              {/* Search Box */}
              <div className="search-box-wrapper">
                <div className="search-box">
                  <span className="search-icon">🔍</span>
                  <input
                    type="text"
                    placeholder="Search by name, destination, or interests..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                  />
                </div>
                
                {/* Filter Toggle Button */}
                <button 
                  className="filter-toggle-btn"
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <span className="filter-icon">🎯</span>
                  <span className="filter-text">Advanced Filters</span>
                  <span className={`filter-arrow ${showFilters ? 'open' : ''}`}>▼</span>
                  {Object.values(filters).filter((v, i) => v !== "all" && v !== "").length > 0 && (
                    <span className="filter-count">{Object.values(filters).filter((v, i) => v !== "all" && v !== "").length}</span>
                  )}
                </button>
              </div>

              {/* Collapsible Cascading Filters */}
              {showFilters && (
              <div className="filters-grid filters-expanded">
                {/* Row 1: Destination and Location */}
                <div className="filter-group">
                  <label className="filter-label">📍 Destination</label>
                  <select
                    value={filters.destination}
                    onChange={(e) => handleFilterChange('destination', e.target.value)}
                    className="filter-select"
                  >
                    {destinations.map(dest => (
                      <option key={dest} value={dest}>
                        {dest === "all" ? "All Destinations" : dest}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="filter-group">
                  <label className="filter-label">🏠 Current Location</label>
                  <select
                    value={filters.location}
                    onChange={(e) => handleFilterChange('location', e.target.value)}
                    className="filter-select"
                  >
                    {locations.map(loc => (
                      <option key={loc} value={loc}>
                        {loc === "all" ? "All Locations" : loc}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Row 2: Budget Range */}
                <div className="filter-group">
                  <label className="filter-label">💰 Min Budget (₹)</label>
                  <input
                    type="number"
                    placeholder="Min"
                    value={filters.minBudget}
                    onChange={(e) => handleFilterChange('minBudget', e.target.value)}
                    className="filter-input"
                    min="0"
                  />
                </div>

                <div className="filter-group">
                  <label className="filter-label">💰 Max Budget (₹)</label>
                  <input
                    type="number"
                    placeholder="Max"
                    value={filters.maxBudget}
                    onChange={(e) => handleFilterChange('maxBudget', e.target.value)}
                    className="filter-input"
                    min="0"
                  />
                </div>

                {/* Row 3: Date Range */}
                <div className="filter-group">
                  <label className="filter-label">📅 Start Date</label>
                  <input
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                    className="filter-input"
                  />
                </div>

                <div className="filter-group">
                  <label className="filter-label">📅 End Date</label>
                  <input
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                    className="filter-input"
                  />
                </div>

                {/* Row 4: Age Range */}
                <div className="filter-group">
                  <label className="filter-label">👤 Min Age</label>
                  <input
                    type="number"
                    placeholder="Min"
                    value={filters.minAge}
                    onChange={(e) => handleFilterChange('minAge', e.target.value)}
                    className="filter-input"
                    min="18"
                    max="100"
                  />
                </div>

                <div className="filter-group">
                  <label className="filter-label">👤 Max Age</label>
                  <input
                    type="number"
                    placeholder="Max"
                    value={filters.maxAge}
                    onChange={(e) => handleFilterChange('maxAge', e.target.value)}
                    className="filter-input"
                    min="18"
                    max="100"
                  />
                </div>

                {/* Row 5: Gender, Connection Status, Trip Status */}
                <div className="filter-group">
                  <label className="filter-label">⚧️ Gender</label>
                  <select
                    value={filters.gender}
                    onChange={(e) => handleFilterChange('gender', e.target.value)}
                    className="filter-select"
                  >
                    <option value="all">All Genders</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="filter-group">
                  <label className="filter-label">🤝 Connection Status</label>
                  <select
                    value={filters.connectionStatus}
                    onChange={(e) => handleFilterChange('connectionStatus', e.target.value)}
                    className="filter-select"
                  >
                    <option value="all">All Status</option>
                    <option value="none">Not Connected</option>
                    <option value="pending">Pending</option>
                    <option value="connected">Connected</option>
                  </select>
                </div>

                <div className="filter-group">
                  <label className="filter-label">🗺️ Trip Status</label>
                  <select
                    value={filters.tripStatus}
                    onChange={(e) => handleFilterChange('tripStatus', e.target.value)}
                    className="filter-select"
                  >
                    <option value="all">All Trips</option>
                    <option value="planning">Planning</option>
                    <option value="active">Active</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>

                {/* Reset Button */}
                <div className="filter-group filter-actions">
                  <button onClick={resetFilters} className="reset-filters-btn">
                    🔄 Reset All Filters
                  </button>
                </div>
              </div>
              )}

              {/* Active Filters Summary */}
              {(searchTerm || Object.values(filters).some((v, i) => 
                i > 0 && v !== "all" && v !== ""
              )) && (
                <div className="active-filters">
                  <span className="active-filters-label">Active Filters:</span>
                  {searchTerm && (
                    <span className="filter-tag">
                      Search: "{searchTerm}"
                      <button onClick={() => setSearchTerm("")}>×</button>
                    </span>
                  )}
                  {filters.destination !== "all" && (
                    <span className="filter-tag">
                      Destination: {filters.destination}
                      <button onClick={() => handleFilterChange('destination', 'all')}>×</button>
                    </span>
                  )}
                  {filters.location !== "all" && (
                    <span className="filter-tag">
                      Location: {filters.location}
                      <button onClick={() => handleFilterChange('location', 'all')}>×</button>
                    </span>
                  )}
                  {filters.minBudget && (
                    <span className="filter-tag">
                      Min Budget: ₹{filters.minBudget}
                      <button onClick={() => handleFilterChange('minBudget', '')}>×</button>
                    </span>
                  )}
                  {filters.maxBudget && (
                    <span className="filter-tag">
                      Max Budget: ₹{filters.maxBudget}
                      <button onClick={() => handleFilterChange('maxBudget', '')}>×</button>
                    </span>
                  )}
                  {filters.startDate && (
                    <span className="filter-tag">
                      From: {new Date(filters.startDate).toLocaleDateString()}
                      <button onClick={() => handleFilterChange('startDate', '')}>×</button>
                    </span>
                  )}
                  {filters.endDate && (
                    <span className="filter-tag">
                      To: {new Date(filters.endDate).toLocaleDateString()}
                      <button onClick={() => handleFilterChange('endDate', '')}>×</button>
                    </span>
                  )}
                  {filters.minAge && (
                    <span className="filter-tag">
                      Min Age: {filters.minAge}
                      <button onClick={() => handleFilterChange('minAge', '')}>×</button>
                    </span>
                  )}
                  {filters.maxAge && (
                    <span className="filter-tag">
                      Max Age: {filters.maxAge}
                      <button onClick={() => handleFilterChange('maxAge', '')}>×</button>
                    </span>
                  )}
                  {filters.gender !== "all" && (
                    <span className="filter-tag">
                      Gender: {filters.gender}
                      <button onClick={() => handleFilterChange('gender', 'all')}>×</button>
                    </span>
                  )}
                  {filters.connectionStatus !== "all" && (
                    <span className="filter-tag">
                      Status: {filters.connectionStatus}
                      <button onClick={() => handleFilterChange('connectionStatus', 'all')}>×</button>
                    </span>
                  )}
                  {filters.tripStatus !== "all" && (
                    <span className="filter-tag">
                      Trip: {filters.tripStatus}
                      <button onClick={() => handleFilterChange('tripStatus', 'all')}>×</button>
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Partners Grid */}
          <div className="partners-section">
            <div className="container">
              {loading ? (
                <div className="loading">Loading partners...</div>
              ) : error ? (
                <div className="error">{error}</div>
              ) : filteredPartners.length > 0 ? (
                <>
                  <div className="results-header">
                    <h2 className="results-title">
                      Available Travel Buddies
                      <span className="results-count">({filteredPartners.length} found)</span>
                    </h2>
                  </div>
                  
                  <div className="partners-grid">
                    {filteredPartners.map((partner) => {
                      const hasTrips = partner.trips && partner.trips.length > 0;
                      const connectionStatus = partner.connection_status;
                      const isConnected = connectionStatus === 'accepted';
                      
                      const avatarSrc = partner.user?.personal_photo 
                        ? (partner.user.personal_photo.startsWith('http') ? partner.user.personal_photo : `${BACKEND_BASE}${partner.user.personal_photo}`)
                        : `https://ui-avatars.com/api/?name=${encodeURIComponent(partner.user?.name || 'User')}&background=random`;
                      
                      return (
                        <div key={partner.id} className="partner-card">
                          <div className="partner-header">
                            <img 
                              src={avatarSrc} 
                              alt={partner.user?.name || 'User'} 
                              className="partner-avatar" 
                            />
                            {isConnected && (
                              <div className="partner-badge connected">✓ Connected</div>
                            )}
                            {connectionStatus === 'pending' && (
                              <div className="partner-badge pending">⏳ Pending</div>
                            )}
                          </div>
                          
                          <div className="partner-body">
                            <h3 className="partner-name">{partner.user?.name || 'Anonymous'}</h3>
                            
                            {/* Rating Display - Clickable or Placeholder */}
                            {partner.average_rating > 0 ? (
                              <div 
                                className="partner-rating clickable-rating"
                                onClick={() => handleViewRatings(partner.user?.id, partner.user?.name)}
                                title="Click to view all ratings"
                              >
                                <span className="rating-stars">
                                  {'⭐'.repeat(Math.round(partner.average_rating))}
                                </span>
                                <span className="rating-value">
                                  {partner.average_rating.toFixed(1)}
                                </span>
                                <span className="rating-count">
                                  ({partner.total_ratings} {partner.total_ratings === 1 ? 'review' : 'reviews'})
                                </span>
                              </div>
                            ) : (
                              <div className="partner-rating-placeholder"></div>
                            )}
                            
                            <p className="partner-age">👤 {partner.user?.age} years • {partner.user?.gender}</p>
                            {partner.user?.current_location && (
                              <p className="partner-location">📍 {partner.user.current_location}</p>
                            )}
                            
                            {/* Trips List - Compact View */}
                            {hasTrips ? (
                              <div className="compact-trips">
                                <h4 className="trips-title">
                                  🗺️ {partner.trips.length} Trip{partner.trips.length > 1 ? 's' : ''}
                                </h4>
                                <div className="trips-carousel">
                                  {partner.trips.map((trip, idx) => {
                                    return (
                                    <div key={trip.id} className="trip-badge-card">
                                      <div className="trip-badge-header">
                                        <span className="trip-destination">📍 {trip.destination}</span>
                                        <span className={`trip-status-badge ${trip.status}`}>
                                          {trip.status}
                                        </span>
                                      </div>
                                      <div className="trip-badge-details">
                                        <span className="trip-detail-mini">📅 {new Date(trip.trip_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                                        <span className="trip-detail-mini">⏱️ {trip.trip_duration}d</span>
                                        <span className="trip-detail-mini">💰 {trip.budget}</span>
                                      </div>
                                      
                                      {/* Always render interests section for debugging */}
                                      <div className="trip-interests-mini">
                                        {trip.interests ? (
                                          <>
                                            {trip.interests.split(',').slice(0, 3).map((interest, i) => (
                                              <span key={i} className="interest-pill">
                                                {interest.trim()}
                                              </span>
                                            ))}
                                            {trip.interests.split(',').length > 3 && (
                                              <span className="interest-pill more">+{trip.interests.split(',').length - 3}</span>
                                            )}
                                          </>
                                        ) : (
                                          <span style={{fontSize: '0.7rem', color: '#999', fontStyle: 'italic'}}>No interests</span>
                                        )}
                                      </div>
                                    </div>
                                    );
                                  })}
                                </div>
                              </div>
                            ) : (
                              <p className="no-trips-msg">No active trips</p>
                            )}

                            {partner.bio && partner.bio.trim() && (
                              <p className="partner-bio-compact">{partner.bio}</p>
                            )}

                            {/* Actions */}
                            <div className="partner-actions">
                              {isConnected ? (
                                <>
                                  <button 
                                    className="view-details-btn"
                                    onClick={() => openContactModal(partner)}
                                  >
                                    📞 View Contact
                                  </button>
                                  {partner.my_rating ? (
                                    <div className="already-rated-badge">
                                      ✓ Rated: {'⭐'.repeat(partner.my_rating)}
                                    </div>
                                  ) : (
                                    <button 
                                      className="rate-btn"
                                      onClick={() => handleRateUser(partner.user.id, partner.user.name, userRole === 'agency' ? 'partner' : 'partner')}
                                      title="Rate this partner"
                                    >
                                      ⭐ Rate
                                    </button>
                                  )}
                                </>
                              ) : connectionStatus === 'pending' ? (
                                <button className="pending-btn" disabled>
                                  {partner.is_sender ? '⏳ Request Sent' : '📩 Respond'}
                                </button>
                              ) : connectionStatus === 'rejected' ? (
                                <button className="rejected-btn" disabled>
                                  ✗ Declined
                                </button>
                              ) : (
                                <button 
                                  className="connect-btn"
                                  onClick={() => openRequestModal(partner)}
                                >
                                  🤝 Connect
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <div className="no-results">
                  <div className="no-results-icon">🧳✈️🌍</div>
                  <h2 className="no-results-title">No Travel Partners Found</h2>
                  <p className="no-results-text">
                    We couldn't find any travel partners matching your criteria at the moment. 
                    Don't worry—new adventurers are joining every day!
                  </p>
                  <div className="no-results-suggestions">
                    <h3>💡 What you can do:</h3>
                    <ul>
                      <li>🔍 Adjust your search filters to see more results</li>
                      <li>🌍 Try different destinations or dates</li>
                      <li>⏰ Check back later for new travel buddies</li>
                      <li>📝 Make sure you've created your own trip to connect with others</li>
                    </ul>
                  </div>
                  <button 
                    className="btn-clear-filters"
                    onClick={resetFilters}
                  >
                    🔄 Clear All Filters
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Received Requests Tab */}
      {activeTab === 'received' && (
        <div className="requests-section">
          <div className="container">
            <h2 className="requests-title">
              {userRole === 'agency' ? 'Received Queries from Partners' : 'Received Connection Requests'}
            </h2>
            {receivedRequests.length > 0 ? (
              <div className="requests-list">
                {receivedRequests.map((request) => {
                  // For agencies: request is a query with partner info
                  // For partners: request has sender.user info
                  const isAgency = userRole === 'agency';
                  const userData = isAgency ? request.partner?.user : request.sender?.user;
                  const avatar = userData?.personal_photo ? (userData.personal_photo.startsWith('http') ? userData.personal_photo : `${BACKEND_BASE}${userData.personal_photo}`) : `https://ui-avatars.com/api/?name=${encodeURIComponent(userData?.name || 'User')}&background=random`;
                  
                  return (
                    <div key={request.id} className="request-card">
                      <div className="request-header">
                        <img 
                          src={avatar}
                          alt={userData?.name || 'User'}
                          className="request-avatar"
                        />
                        <div className="request-info">
                          <h3>{userData?.name || 'Unknown'}</h3>
                          <p className="request-details">
                            👤 {userData?.age || 'N/A'} years • {userData?.gender || 'N/A'}
                          </p>
                          {userData?.current_location && (
                            <p className="request-location">📍 From: {userData.current_location}</p>
                          )}
                        </div>
                        <span className={`request-status ${request.status}`}>{request.status}</span>
                      </div>
                      <div className="request-message">
                        {isAgency ? request.query_message : request.message}
                      </div>
                      {request.status === 'pending' && (
                        <div className="request-actions">
                          <button 
                            className="accept-btn"
                            onClick={() => handleAcceptRequest(request.id)}
                          >
                            ✓ Accept
                          </button>
                          <button 
                            className="reject-btn"
                            onClick={() => handleRejectRequest(request.id)}
                          >
                            ✗ Reject
                          </button>
                        </div>
                      )}
                      {request.status === 'accepted' && (
                        <div className="request-actions">
                          <button 
                            className="view-contact-btn"
                            onClick={() => {
                              setSelectedContact(isAgency ? request.partner : request.sender);
                              setShowContactModal(true);
                            }}
                          >
                            📞 View Contact
                          </button>
                          {(isAgency ? request.partner?.my_rating : request.sender?.my_rating) ? (
                            <div className="already-rated-badge">
                              ✓ Rated: {'⭐'.repeat(isAgency ? request.partner.my_rating : request.sender.my_rating)}
                            </div>
                          ) : (
                            <button 
                              className="rate-btn"
                              onClick={() => handleRateUser(
                                userData?.id,
                                userData?.name,
                                userRole === 'agency' ? 'partner' : 'partner'
                              )}
                              title={`Rate ${userData?.name}`}
                            >
                              ⭐ Rate
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="no-results">
                <div className="no-results-icon">📬💌✨</div>
                <h2 className="no-results-title">
                  {userRole === 'agency' ? 'No Received Queries Yet' : 'No Received Requests Yet'}
                </h2>
                <p className="no-results-text">
                  {userRole === 'agency' 
                    ? "You haven't received any queries from partners yet. Make your agency profile attractive to get more inquiries!"
                    : "You haven't received any connection requests from other travelers yet. Make your profile stand out to attract travel buddies!"
                  }
                </p>
                <div className="no-results-suggestions">
                  <h3>💡 Tips to get noticed:</h3>
                  <ul>
                    {userRole === 'agency' ? (
                      <>
                        <li>✨ Complete your agency profile with services and packages</li>
                        <li>🗺️ Add detailed location and contact information</li>
                        <li>💬 Send requests to potential clients actively</li>
                        <li>🏆 Showcase your best destinations and offers</li>
                      </>
                    ) : (
                      <>
                        <li>✨ Complete your profile with an attractive photo</li>
                        <li>🗺️ Add detailed trip information and interests</li>
                        <li>💬 Be active and send requests to potential partners</li>
                        <li>📅 Create trips to popular destinations</li>
                      </>
                    )}
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sent Requests Tab */}
      {activeTab === 'sent' && (
        <div className="requests-section">
          <div className="container">
            <h2 className="requests-title">
              {userRole === 'agency' ? 'Sent Requests to Partners' : 'Sent Connection Requests'}
            </h2>
            {sentRequests.length > 0 ? (
              <div className="requests-list">
                {sentRequests.map((request) => {
                  // For agencies: request has partner info
                  // For partners: request has receiver.user info
                  const isAgency = userRole === 'agency';
                  const userData = isAgency ? request.partner?.user : request.receiver?.user;
                  const avatar = userData?.personal_photo ? (userData.personal_photo.startsWith('http') ? userData.personal_photo : `${BACKEND_BASE}${userData.personal_photo}`) : `https://ui-avatars.com/api/?name=${encodeURIComponent(userData?.name || 'User')}&background=random`;
                  
                  return (
                    <div key={request.id} className="request-card">
                      <div className="request-header">
                        <img 
                          src={avatar}
                          alt={userData?.name || 'User'}
                          className="request-avatar"
                        />
                        <div className="request-info">
                          <h3>{userData?.name || 'Unknown'}</h3>
                          <p className="request-details">
                            👤 {userData?.age || 'N/A'} years • {userData?.gender || 'N/A'}
                          </p>
                          {userData?.current_location && (
                            <p className="request-location">📍 From: {userData.current_location}</p>
                          )}
                        </div>
                        <span className={`request-status ${request.status}`}>{request.status}</span>
                      </div>
                      <div className="request-message">
                        {isAgency ? request.query_message : request.message}
                      </div>
                      {request.status === 'accepted' && (
                        <div className="request-actions">
                          <button 
                            className="view-contact-btn"
                            onClick={() => {
                              setSelectedContact(isAgency ? request.partner : request.receiver);
                              setShowContactModal(true);
                            }}
                          >
                            📞 View Contact
                          </button>
                          {(isAgency ? request.partner?.my_rating : request.receiver?.my_rating) ? (
                            <div className="already-rated-badge">
                              ✓ Rated: {'⭐'.repeat(isAgency ? request.partner.my_rating : request.receiver.my_rating)}
                            </div>
                          ) : (
                            <button 
                              className="rate-btn"
                              onClick={() => handleRateUser(
                                userData?.id,
                                userData?.name,
                                userRole === 'agency' ? 'partner' : 'partner'
                              )}
                              title={`Rate ${userData?.name}`}
                            >
                              ⭐ Rate
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="no-results">
                <div className="no-results-icon">📤🤝🌟</div>
                <h2 className="no-results-title">
                  {userRole === 'agency' ? 'No Sent Requests Yet' : 'No Sent Requests Yet'}
                </h2>
                <p className="no-results-text">
                  {userRole === 'agency'
                    ? "You haven't sent any requests to partners yet. Start connecting with potential clients!"
                    : "You haven't sent any connection requests to other travelers yet. Start building your travel network today!"
                  }
                </p>
                <div className="no-results-suggestions">
                  <h3>💡 Get started:</h3>
                  <ul>
                    {userRole === 'agency' ? (
                      <>
                        <li>🔍 Browse partners looking for travel services</li>
                        <li>💬 Send personalized requests with your offerings</li>
                        <li>🤝 Build relationships with potential clients</li>
                        <li>✈️ Help them plan their perfect trip</li>
                      </>
                    ) : (
                      <>
                        <li>🔍 Browse the "Find Partners" tab to discover travelers</li>
                        <li>💬 Send personalized connection requests</li>
                        <li>🤝 Build your travel network</li>
                        <li>✈️ Start planning your next adventure together</li>
                      </>
                    )}
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      </>
      )}

      {/* Request Modal */}
      {showRequestModal && (
        <div className="modal-overlay" onClick={() => setShowRequestModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Send Connection Request</h2>
            <p>To: {selectedPartner?.user.name}</p>
            <textarea
              value={requestMessage}
              onChange={(e) => setRequestMessage(e.target.value)}
              placeholder="Enter your message..."
              rows="4"
              className="request-textarea"
            />
            <div className="modal-actions">
              <button
                className="cancel-btn"
                onClick={() => setShowRequestModal(false)}
                disabled={sendingRequest}
              >
                Cancel
              </button>
              <button
                className="send-btn"
                onClick={handleSendRequest}
                disabled={sendingRequest}
              >
                {sendingRequest ? "Sending..." : "Send Request"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contact Details Modal */}
      {showContactModal && selectedContact && (
        <div className="modal-overlay" onClick={() => setShowContactModal(false)}>
          <div className="modal-content contact-modal" onClick={(e) => e.stopPropagation()}>
            <div className="contact-modal-header">
              <img 
                src={selectedContact.user?.personal_photo ? `${BACKEND_BASE}${selectedContact.user.personal_photo}` : `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedContact.user?.name || 'User')}&background=random`} 
                alt={selectedContact.user?.name}
                className="contact-modal-avatar"
              />
              <h2>{selectedContact.user?.name}</h2>
              <p className="contact-modal-subtitle">
                {selectedContact.user?.age} years • {selectedContact.user?.gender}
              </p>
            </div>
            
            <div className="contact-modal-body">
              <div className="contact-detail-item">
                <div className="contact-detail-icon">📧</div>
                <div className="contact-detail-info">
                  <label>Email Address</label>
                  <a href={`mailto:${selectedContact.user?.email}`} className="contact-detail-value">
                    {selectedContact.user?.email}
                  </a>
                </div>
              </div>
              
              <div className="contact-detail-item">
                <div className="contact-detail-icon">📱</div>
                <div className="contact-detail-info">
                  <label>Phone Number</label>
                  <a href={`tel:${selectedContact.user?.phone}`} className="contact-detail-value">
                    {selectedContact.user?.phone}
                  </a>
                </div>
              </div>

              {/* Display all trips */}
              {selectedContact.trips && selectedContact.trips.length > 0 && (
                <>
                  <div className="contact-detail-item">
                    <div className="contact-detail-icon">📍</div>
                    <div className="contact-detail-info">
                      <label>Destination{selectedContact.trips.length > 1 ? 's' : ''}</label>
                      {selectedContact.trips.map((trip, index) => (
                        <span key={index} className="contact-detail-value">
                          {trip.destination}{index < selectedContact.trips.length - 1 ? ', ' : ''}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="contact-detail-item">
                    <div className="contact-detail-icon">📅</div>
                    <div className="contact-detail-info">
                      <label>Trip Date{selectedContact.trips.length > 1 ? 's' : ''}</label>
                      {selectedContact.trips.map((trip, index) => (
                        <span key={index} className="contact-detail-value" style={{display: 'block', marginBottom: index < selectedContact.trips.length - 1 ? '8px' : '0'}}>
                          {trip.destination}: {new Date(trip.trip_date).toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          })}
                        </span>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="modal-actions">
              <button
                className="close-modal-btn"
                onClick={() => setShowContactModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rating Modal */}
      <RatingModal
        show={showRatingModal}
        onClose={() => setShowRatingModal(false)}
        onSubmit={handleSubmitRating}
        userName={ratingTarget?.userName || ""}
        userRole={ratingTarget?.userRole || "partner"}
      />

      {/* Ratings View Modal */}
      <RatingsViewModal
        show={showRatingsViewModal}
        onClose={() => setShowRatingsViewModal(false)}
        userId={ratingsViewTarget?.userId}
        userName={ratingsViewTarget?.userName}
      />

      {/* Custom Alert Modal */}
      <CustomAlert
        show={alertConfig.show}
        onClose={closeAlert}
        title={alertConfig.title}
        message={alertConfig.message}
        type={alertConfig.type}
      />
    </div>
  );
}
